import { Dispatch, SetStateAction } from "react";
import { Field, FieldProps, Form, Formik, FormikProps, FormikValues, getIn } from "formik";
import { styled } from "@mui/system";
import Box from "@mui/material/Box";
import { Typography, TypographyProps } from "@mui/material";
import Button from "@ds/components/Button";
import Text from "@ds/components/Text";
import FormControl from "@ds/components/FormControl";
import InputBase from "@ds/components/InputBase";
import VisiblePassword from "@fragments/VisiblePassword";
import { greenVector, greyVector } from "@config/images";
import useResponsive from "@helpers/useResponsive";
import { useTranslation, withTranslation } from "next-i18next";
import useAction from "./hooks/useAction";
import { TChangePassword } from "./type";
import validateForm from "./validate";

const StyleLabelValidation = styled(Typography)<TypographyProps>(() => ({
  fontFamily: "Open Sans, sans-serif",
  fontStyle: "normal",
  fontWeight: 600,
  fontSize: "16px",
  margin: "10px 0",
  fontColor: "#2E434D",
}));

const PasswordChangeLayout = (props: {
  setCloseDialog?: Dispatch<SetStateAction<boolean>>;
  resetType: string;
}) => {
  const { setCloseDialog, resetType } = props;
  const {
    initialValueForm,
    onSubmit,
    onNewPasswordValidate,
    onReEnterPasswordValidate,
    handleClickPass,
    visiblePass,
    visibleRePass,
    verifyPass,
    errorMessage,
    isLoad,
    reVerifyPass,
    isInputPass,
  } = useAction(setCloseDialog, resetType);

  const isMD = useResponsive("md");
  const { t } = useTranslation("forgot");

  return (
    <Formik
      initialValues={initialValueForm}
      onSubmit={onSubmit}
      validationSchema={validateForm({ ...verifyPass, ...reVerifyPass })}
    >
      {(props: FormikProps<Partial<TChangePassword>>) => {
        return (
          <Box maxWidth={isMD ? "100%" : 360}>
            <Form>
              <Box textAlign={isMD ? "start" : "center"} mx={isMD ? 0 : "6%"} mb={4}>
                <Text color="grey-main">{t("message")}</Text>
              </Box>
              {resetType !== "change_password" && errorMessage && (
                <Box my={2} textAlign={isMD ? "start" : "center"}>
                  <Text color="primary-main">{errorMessage}</Text>
                </Box>
              )}
              <Field
                name="newPassword"
                children={({ field, form }: FieldProps<FormikValues>) => {
                  const { value } = field;
                  const { touched, errors, setFieldValue } = form;
                  const isError =
                    (getIn(touched, field.name) && getIn(errors, field.name)) || !!errorMessage;
                  return (
                    <FormControl
                      label={t("form.label_password")}
                      error={isError}
                      form={form}
                      field={field}
                    >
                      <InputBase
                        dataTestID="password-id"
                        placeholder={t("form.placeholder")}
                        type={visiblePass ? "text" : "password"}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue(field.name, event.target.value);
                          onNewPasswordValidate(event.target.value);
                        }}
                        endAdornment={
                          <VisiblePassword
                            visiblePass={visiblePass}
                            handleClickPass={() => handleClickPass(true)}
                          />
                        }
                      />
                      <Box my={2} textAlign="start">
                        {resetType === "change_password" &&
                          value?.length > 0 &&
                          value?.length <= 8 && (
                            <Text color="general-main">{t("error.msg_min_char")}</Text>
                          )}
                      </Box>
                    </FormControl>
                  );
                }}
              />
              <Field
                name="reEnterNewPassword"
                children={({ field, form, hint }: any) => {
                  const { touched, errors, setFieldValue } = form;
                  const isError =
                    (getIn(touched, field.name) && getIn(errors, field.name)) || !!errorMessage;
                  return (
                    <FormControl
                      label={t("form.label_confirm_password")}
                      error={isError}
                      form={form}
                      field={field}
                      hint={hint}
                    >
                      <InputBase
                        dataTestID="rePassword-id"
                        placeholder={t("form.placeholder_confirm")}
                        type={visibleRePass ? "text" : "password"}
                        onChange={(event: { target: { value: string } }) => {
                          setFieldValue([field.name], event.target.value);
                          onReEnterPasswordValidate(event.target.value);
                        }}
                        endAdornment={
                          <VisiblePassword
                            visiblePass={visibleRePass}
                            handleClickPass={() => handleClickPass(false)}
                          />
                        }
                      />
                      <Box my={2} textAlign="start">
                        {resetType === "change_password" && errorMessage && (
                          <Text color="primary-main">{t("error.msg_confirm")}</Text>
                        )}
                      </Box>
                    </FormControl>
                  );
                }}
              />
              <Box>
                <Box display="flex" alignItems="center" gap="10px">
                  <img
                    src={
                      (isInputPass && verifyPass.isMin8) || (!isInputPass && reVerifyPass.isReMin8)
                        ? greenVector
                        : greyVector
                    }
                    alt=""
                    height={20}
                    width={20}
                  />
                  <StyleLabelValidation>{t("form.flag_1")}</StyleLabelValidation>
                </Box>
                <Box display="flex" alignItems="center" gap="10px">
                  <img
                    src={
                      (isInputPass && verifyPass.isAlpha) ||
                      (!isInputPass && reVerifyPass.isReAlpha)
                        ? greenVector
                        : greyVector
                    }
                    alt=""
                    height={20}
                    width={20}
                  />
                  <StyleLabelValidation>{t("form.flag_2")}</StyleLabelValidation>
                </Box>
                <Box display="flex" alignItems="center" gap="10px">
                  <img
                    src={
                      (isInputPass && verifyPass.isNum) || (!isInputPass && reVerifyPass.isReNum)
                        ? greenVector
                        : greyVector
                    }
                    alt=""
                    height={20}
                    width={20}
                  />
                  <StyleLabelValidation>{t("form.flag_3")}</StyleLabelValidation>
                </Box>
              </Box>
              <Box my={4}>
                <Button
                  disabled={!(props?.dirty && props?.isValid)}
                  loading={isLoad && props?.dirty && props?.isValid}
                  size="large"
                  type="submit"
                  width="100%"
                >
                  <Text color={!props?.dirty && props?.isValid ? "grey-main" : "white"}>
                    {t("form.submit")}
                  </Text>
                </Button>
              </Box>
            </Form>
          </Box>
        );
      }}
    </Formik>
  );
};

export default withTranslation(["forgot"])(PasswordChangeLayout);
